import React, { useEffect, useMemo, useState } from 'react';
import AdminShell from '../components/admin/AdminShell';
import PaystackSubaccountSetup from '../components/admin/PaystackSubaccountSetup';
import {
  fetchAdminCompanyPayoutReviewDetail,
  fetchAdminCompanyPayoutReviews,
  isSuperAdminRole,
  updateAdminCompanyPayoutPaystackSubaccount,
  updateAdminCompanyPayoutStatus,
} from '../services/api/admin';
import { getAdminAuthUser } from '../services/storage/adminFlow';
import type { AdminCompanyPayoutReview, PaystackSubaccountUpdatePayload } from '../types/admin';
import { formatDateTime } from '../utils/companyOwner';
import '../styles/register.css';
import '../styles/company-owner.css';
import '../styles/admin.css';

const STATUS_OPTIONS = ['PENDING', 'ACTIVE', 'REQUIRES_UPDATE', 'REJECTED', 'MISSING'];
const REVIEW_ACTIONS = [
  { label: 'Approve payout setup', value: 'ACTIVE' },
  { label: 'Needs update', value: 'REQUIRES_UPDATE' },
  { label: 'Reject payout setup', value: 'REJECTED' },
] as const;

function isPaystackVerified(review: AdminCompanyPayoutReview | null): boolean {
  return review?.paystackSubaccountStatus === 'VERIFIED' && Boolean(review.paystackSubaccountCode?.trim());
}

function getPaystackReadinessMessage(review: AdminCompanyPayoutReview | null): string {
  if (!review) return '';

  const missingItems = [
    review.paystackSubaccountStatus === 'VERIFIED' ? '' : 'set Paystack status to VERIFIED',
    review.paystackSubaccountCode?.trim() ? '' : 'save an ACCT_ subaccount code',
  ].filter(Boolean);

  if (!missingItems.length) return '';

  return `Before approving, ${missingItems.join(' and ')}.`;
}

const AdminPayoutReviewsPage: React.FC = () => {
  const adminUser = useMemo(() => getAdminAuthUser(), []);
  const isSuperAdmin = isSuperAdminRole(adminUser?.roles);
  const [items, setItems] = useState<AdminCompanyPayoutReview[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedReview, setSelectedReview] = useState<AdminCompanyPayoutReview | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  const [filters, setFilters] = useState({
    status: 'PENDING',
    query: '',
  });
  const [reviewReason, setReviewReason] = useState('');
  const selectedPaystackVerified = isPaystackVerified(selectedReview);
  const paystackReadinessMessage = getPaystackReadinessMessage(selectedReview);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError('');

      try {
        const listResponse = await fetchAdminCompanyPayoutReviews({
          status: filters.status || 'PENDING',
          ...(filters.query.trim() ? { query: filters.query.trim() } : {}),
          page,
          size: pageSize,
          sort: 'updated,desc',
        });

        if (!mounted) return;
        setItems(listResponse.items);
        setTotalPages(listResponse.totalPages);
        setTotalElements(listResponse.totalElements);

        const nextSelectedId =
          listResponse.items.find((item) => item.companyId === selectedCompanyId)?.companyId ||
          listResponse.items[0]?.companyId ||
          '';
        setSelectedCompanyId(nextSelectedId);
      } catch (loadError: any) {
        if (!mounted) return;
        setError(loadError.message || 'Could not load payout reviews.');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      mounted = false;
    };
  }, [filters, page, pageSize, selectedCompanyId]);

  useEffect(() => {
    let mounted = true;

    const loadDetail = async () => {
      if (!selectedCompanyId) {
        setSelectedReview(null);
        return;
      }

      setDetailLoading(true);
      setActionError('');

      try {
        const detail = await fetchAdminCompanyPayoutReviewDetail(selectedCompanyId);
        if (!mounted) return;
        setSelectedReview(detail);
      } catch (detailError: any) {
        if (!mounted) return;
        setActionError(detailError.message || 'Could not load payout review details.');
      } finally {
        if (mounted) {
          setDetailLoading(false);
        }
      }
    };

    void loadDetail();
    return () => {
      mounted = false;
    };
  }, [selectedCompanyId]);

  const handleUpdateStatus = async (status: (typeof REVIEW_ACTIONS)[number]['value']) => {
    if (!selectedReview?.companyId || !adminUser?.id) return;
    if (status === 'ACTIVE' && !selectedPaystackVerified) {
      setActionError('Company payout setup cannot be activated until Paystack status is VERIFIED and an ACCT_ subaccount code has been saved.');
      return;
    }

    setActionLoading(status);
    setActionError('');
    setActionSuccess('');

    try {
      const updated = await updateAdminCompanyPayoutStatus({
        companyUuid: selectedReview.companyId,
        actorUserId: adminUser.id,
        status,
        reason: reviewReason,
      });

      setSelectedReview(updated);
      setItems((current) =>
        current.map((item) => (item.companyId === updated.companyId ? { ...item, ...updated } : item)),
      );
      setActionSuccess(`Payout review updated to ${status}.`);
      /* Unconditional: the three actions an admin can take are ACTIVE,
         REQUIRES_UPDATE and REJECTED, so the PENDING check that used to guard
         this could never be false. Clearing the reason is what already
         happened on every path through here. */
      setReviewReason('');
    } catch (updateError: any) {
      setActionError(updateError.message || 'Could not update payout review status.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSavePaystackSetup = async (payload: PaystackSubaccountUpdatePayload) => {
    if (!selectedReview?.companyId) return;

    setActionError('');
    setActionSuccess('');

    const updated = await updateAdminCompanyPayoutPaystackSubaccount(selectedReview.companyId, payload);
    setSelectedReview(updated);
    setItems((current) =>
      current.map((item) => (item.companyId === updated.companyId ? { ...item, ...updated } : item)),
    );
    setActionSuccess('Company Paystack subaccount setup saved.');
  };

  return (
    <AdminShell
      badge="Payout Reviews"
      copy="Review company payout setup submissions, inspect bank evidence, and apply the final admin status."
      title="Company payout reviews"
    >
      {error ? <div className="register-inline-error">{error}</div> : null}
      {actionSuccess ? <div className="register-inline-success">{actionSuccess}</div> : null}
      {actionError ? <div className="register-inline-error">{actionError}</div> : null}

      <section className="admin-panel" style={{ marginBottom: '18px' }}>
        <div className="admin-panel-header">
          <div>
            <h2 className="admin-panel-title">Review filters</h2>
            <p className="admin-panel-copy">Search payout submissions by company identity or filter by payout review state.</p>
          </div>
        </div>
        <div className="admin-toolbar">
          <input
            className="register-input admin-toolbar-wide"
            onChange={(event) => {
              setPage(0);
              setFilters((current) => ({ ...current, query: event.target.value }));
            }}
            placeholder="Search by company name, trading name, email, or registration number"
            value={filters.query}
          />
          <select
            className="register-input"
            onChange={(event) => {
              setPage(0);
              setFilters((current) => ({ ...current, status: event.target.value }));
            }}
            value={filters.status}
          >
            {STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {status.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </div>
      </section>

      <div className="admin-grid admin-grid-main admin-payout-reviews-layout">
        <section className="admin-panel">
          <div className="admin-panel-header">
            <div>
              <h2 className="admin-panel-title">Pending companies</h2>
              <p className="admin-panel-copy">Select a company to inspect the submitted payout destination and bank letter.</p>
            </div>
          </div>

          {loading ? (
            <div className="register-loader" />
          ) : (
            <>
              <div className="admin-list">
                {items.map((item) => (
                  <button
                    className="admin-list-card"
                    key={item.companyId}
                    onClick={() => setSelectedCompanyId(item.companyId)}
                    style={{
                      borderColor: selectedCompanyId === item.companyId ? '#0f766e' : undefined,
                      textAlign: 'left',
                    }}
                    type="button"
                  >
                    <strong>{item.companyName || 'Company pending'}</strong>
                    <small>{item.primaryEmail || 'No company email supplied'}</small>
                    <small>{item.registrationNumber || 'Registration number not supplied'}</small>
                    <div className="admin-pill-row">
                      <span className="admin-pill">{item.payoutStatus || 'MISSING'}</span>
                      <span className="admin-pill">{item.bankLetterPresent ? 'Bank letter uploaded' : 'No bank letter'}</span>
                    </div>
                  </button>
                ))}
                {!items.length ? <p className="admin-empty">No company payout reviews match this filter right now.</p> : null}
              </div>

              <div className="admin-pagination">
                <span>{totalElements} companies</span>
                <button
                  className="register-secondary-button"
                  disabled={page === 0}
                  onClick={() => setPage((current) => Math.max(current - 1, 0))}
                  type="button"
                >
                  Previous
                </button>
                <span>
                  Page {page + 1} of {Math.max(totalPages, 1)}
                </span>
                <button
                  className="register-secondary-button"
                  disabled={page + 1 >= Math.max(totalPages, 1)}
                  onClick={() => setPage((current) => current + 1)}
                  type="button"
                >
                  Next
                </button>
              </div>
            </>
          )}
        </section>

        <section className="admin-panel">
          <div className="admin-panel-header">
            <div>
              <h2 className="admin-panel-title">Payout review detail</h2>
              <p className="admin-panel-copy">Review the payout destination and apply the final status once the submission has been checked.</p>
              <p className="admin-panel-copy">Company payout setup cannot be activated until Paystack status is VERIFIED.</p>
            </div>
          </div>

          {detailLoading ? (
            <div className="register-loader" />
          ) : selectedReview ? (
            <>
              <div className="admin-detail-grid">
                <div className="admin-detail-card"><span>Company</span><strong>{selectedReview.companyName || 'Not supplied'}</strong></div>
                <div className="admin-detail-card"><span>Primary email</span><strong>{selectedReview.primaryEmail || 'Not supplied'}</strong></div>
                <div className="admin-detail-card"><span>Registration number</span><strong>{selectedReview.registrationNumber || 'Not supplied'}</strong></div>
                <div className="admin-detail-card"><span>Payout status</span><strong>{selectedReview.payoutStatus || 'MISSING'}</strong></div>
                <div className="admin-detail-card"><span>Account holder</span><strong>{selectedReview.accountHolderName || 'Not supplied'}</strong></div>
                <div className="admin-detail-card"><span>Bank</span><strong>{selectedReview.bankName || 'Not supplied'}</strong></div>
                <div className="admin-detail-card"><span>Account type</span><strong>{selectedReview.accountType || 'Not supplied'}</strong></div>
                <div className="admin-detail-card"><span>Masked account</span><strong>{selectedReview.maskedAccountNumber || 'Not supplied'}</strong></div>
                <div className="admin-detail-card"><span>Branch code</span><strong>{selectedReview.branchCode || 'Not supplied'}</strong></div>
                <div className="admin-detail-card"><span>Bank letter</span><strong>{selectedReview.bankLetterPresent ? 'Uploaded' : 'Missing'}</strong></div>
                <div className="admin-detail-card"><span>Paystack status</span><strong>{selectedReview.paystackSubaccountStatus || 'NOT CREATED'}</strong></div>
                <div className="admin-detail-card"><span>Paystack subaccount</span><strong>{selectedReview.paystackSubaccountCode || 'Not captured'}</strong></div>
                <div className="admin-detail-card"><span>Letter uploaded</span><strong>{formatDateTime(selectedReview.bankLetterUploadedAt)}</strong></div>
                <div className="admin-detail-card"><span>Last updated</span><strong>{formatDateTime(selectedReview.updatedAt)}</strong></div>
              </div>

              {!selectedPaystackVerified ? (
                <div className="register-inline-error" style={{ marginTop: '16px' }}>
                  Paystack setup is not verified. {paystackReadinessMessage}
                </div>
              ) : null}

              <div className="admin-inline-actions" style={{ marginTop: '16px' }}>
                {selectedReview.bankLetterDownloadUrl ? (
                  <a className="register-secondary-button" href={selectedReview.bankLetterDownloadUrl} rel="noreferrer" target="_blank">
                    Open bank letter
                  </a>
                ) : (
                  <span className="admin-muted-note">No bank letter URL is available for this company yet.</span>
                )}
              </div>

              <div style={{ marginTop: '18px' }}>
                <PaystackSubaccountSetup
                  copy="Save the Paystack Dashboard subaccount details for this company payout destination before activating payout setup."
                  embedded
                  onSave={handleSavePaystackSetup}
                  source={selectedReview}
                />
              </div>

              <div className="register-field" style={{ marginTop: '18px' }}>
                <label className="register-label" htmlFor="payoutReviewReason">
                  Review reason or note
                </label>
                <textarea
                  className="register-input"
                  id="payoutReviewReason"
                  onChange={(event) => setReviewReason(event.target.value)}
                  placeholder="Explain why this payout setup was approved, rejected, or sent back for updates."
                  rows={4}
                  value={reviewReason}
                />
              </div>

              <div className="admin-inline-actions" style={{ marginTop: '16px' }}>
                {REVIEW_ACTIONS.map((action) => (
                  <button
                    className="register-secondary-button"
                    disabled={!isSuperAdmin || Boolean(actionLoading) || (action.value === 'ACTIVE' && !selectedPaystackVerified)}
                    key={action.value}
                    onClick={() => handleUpdateStatus(action.value)}
                    type="button"
                  >
                    {actionLoading === action.value ? 'Saving...' : action.label}
                  </button>
                ))}
              </div>

              {!isSuperAdmin ? (
                <p className="admin-muted-note" style={{ marginTop: '16px' }}>
                  Viewing payout review details is allowed for admins, but changing payout status is restricted to super admin accounts.
                </p>
              ) : null}
            </>
          ) : (
            <p className="admin-empty">Select a company payout review to inspect the submitted details.</p>
          )}
        </section>
      </div>
    </AdminShell>
  );
};

export default AdminPayoutReviewsPage;
